import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { NoteCreateBody, NotePatchBody, EventNote } from '../types'

export function useNotes(eventId: number) {
  return useQuery({
    queryKey: ['notes', eventId],
    queryFn: () => ApiClient.notes(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
    // Light poll so multiple managers see each other's notes within 30s
    // without websockets. Cheap query against an index.
    refetchInterval: 30_000
  })
}

export function useCreateNote(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: NoteCreateBody) => ApiClient.createNote(eventId, body),
    onSuccess: (note: EventNote) => {
      qc.setQueryData<EventNote[]>(['notes', eventId], (cur) =>
        cur ? sortNotes([note, ...cur.filter((n) => n.id !== note.id)]) : [note]
      )
    }
  })
}

export function usePatchNote(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteId, body }: { noteId: number; body: NotePatchBody }) =>
      ApiClient.patchNote(eventId, noteId, body),
    onSuccess: (note: EventNote) => {
      qc.setQueryData<EventNote[]>(['notes', eventId], (cur) =>
        cur ? sortNotes(cur.map((n) => (n.id === note.id ? note : n))) : [note]
      )
    }
  })
}

export function usePinNote(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteId, pinnedLabel }: { noteId: number; pinnedLabel: string | null }) =>
      ApiClient.pinNote(eventId, noteId, pinnedLabel),
    onSuccess: (note: EventNote) => {
      qc.setQueryData<EventNote[]>(['notes', eventId], (cur) =>
        cur ? sortNotes(cur.map((n) => (n.id === note.id ? note : n))) : [note]
      )
    }
  })
}

export function useUnpinNote(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (noteId: number) => ApiClient.unpinNote(eventId, noteId),
    onSuccess: (note: EventNote) => {
      qc.setQueryData<EventNote[]>(['notes', eventId], (cur) =>
        cur ? sortNotes(cur.map((n) => (n.id === note.id ? note : n))) : [note]
      )
    }
  })
}

export function useDeleteNote(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (noteId: number) => ApiClient.deleteNote(eventId, noteId).then(() => noteId),
    onSuccess: (noteId) => {
      qc.setQueryData<EventNote[]>(['notes', eventId], (cur) =>
        cur ? cur.filter((n) => n.id !== noteId) : []
      )
    }
  })
}

function sortNotes(notes: EventNote[]): EventNote[] {
  return [...notes].sort((a, b) => {
    if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1
    return b.createdAt.localeCompare(a.createdAt)
  })
}
